import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import { isValidNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import {
  catchError,
  concat,
  distinctUntilChanged,
  finalize,
  map,
  of,
  Subscription,
  switchMap,
  take,
  takeWhile,
  tap,
  timer
} from 'rxjs';
import { BaseComponent } from '~console/app/~shared/abstract-components/base.component';
import { ROUTES } from '~console/app/~shared/constants/routes.constants';
import { LoadListener } from '~console/app/~shared/utils/rxjs';
import {
  parseMaskToNumber,
  removeNilProperties
} from '~console/app/~shared/utils/utils';
import {
  phoneNumberValidator,
  validatePhoneNumber
} from '~console/app/~shared/utils/validators';
import { attachDialCodePrefill } from '~console/app/~shared/utils/phone.utils';
import {
  PaymentCheckoutParams,
  PaymentCreationParams,
  PaymentMethod,
  PaymentSource,
  TransactionResultStatus
} from '~core/models/payment.models';
import { ReferenceData } from '~core/models/reference-data.models';
import { ConstantsService } from '~core/services/constants.service';
import { PaymentService } from '~core/services/payment.service';
import { ReferenceDataService } from '~core/services/reference-data.service';
import { MatStepper } from '@angular/material/stepper';
import { DEFAULT_STICKER_NUMBERS } from '~core/constants/global.constants';

type PaymentCheckoutForm = FormGroup<{
  paymentMethod: FormControl<PaymentMethod | null>;
  phoneNumber: FormControl<string>;
  amount: FormGroup<{
    amount: FormControl<number | null>;
    currency: FormControl<string>;
  }>;
  redirectUrl: FormControl<string>;
  invoice: FormGroup<{
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    city: FormControl<string | null>;
    country: FormControl<string>;
    district: FormControl<string | null>;
    addressLine: FormControl<string>;
  }>;
  source: FormControl<PaymentSource>;
}>;

enum FormStep {
  step1 = 0,
  step2 = 1,
  step3 = 2
}

type StepState = 'number' | 'edit' | 'done';

@Component({
  selector: 'buy-stickers-form',
  templateUrl: './buy-stickers-form.component.html',
  styleUrls: ['./buy-stickers-form.component.scss'],
  standalone: false
})
export class BuyStickersFormComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  private readonly _referenceDataService = inject(ReferenceDataService);
  private readonly _constantService = inject(ConstantsService);
  private _paymentService = inject(PaymentService);

  protected readonly _stepper = viewChild.required<MatStepper>('stepper');

  intlInputPhone = viewChild<ElementRef>('phoneNumberInput');
  phoneNumberIntlPluginRef?: intlTelInput.Plugin;
  citiesListener: LoadListener<ReferenceData[]> | undefined;
  districtsListener: LoadListener<ReferenceData[]> | undefined;

  selectedPaymentMethod: string = '';
  states: StepState[] = [];

  isSubmitting = signal(false);
  isLocationLinkInvalidFormat = signal(false);
  isLocationLinkGeocodingFailed = signal(false);
  currentStepIndex = signal<FormStep>(FormStep.step1);
  paymentId = signal<string | null>(null);
  transactionStatus = signal<TransactionResultStatus | null>(null);
  hasUnsavedChanges = signal<boolean>(false);

  protected readonly mobileMoneyProviders = [
    {
      id: PaymentMethod.ORANGE_MONEY,
      name: 'Orange Money',
      icon: '/assets/icons/orange.png',
      alt: 'Orange Money'
    },
    {
      id: PaymentMethod.MTN_MONEY,
      name: 'MTN Momo',
      icon: '/assets/icons/mtn.png',
      alt: 'MTN Momo'
    },
    {
      id: PaymentMethod.MOOV_MONEY,
      name: 'Moov Money',
      icon: '/assets/icons/moov.png',
      alt: 'Moov Money'
    }
  ];

  form: PaymentCheckoutForm = new FormGroup({
    paymentMethod: new FormControl<PaymentMethod | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    redirectUrl: new FormControl<string>(
      `${this._constantService.environment.redirectUrl}${ROUTES.stickersCheckoutSucceed}`,
      {
        nonNullable: true
      }
    ),
    phoneNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        phoneNumberValidator({ country: this._constantService.defaultCountry })
      ]
    }),
    amount: new FormGroup({
      amount: new FormControl<number | null>(null, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(1)]
      }),
      currency: new FormControl<string>(this._constantService.defaultCurrency, {
        nonNullable: true,
        validators: [Validators.required]
      })
    }),
    invoice: new FormGroup({
      firstName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      lastName: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      city: new FormControl<string | null>(null, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      country: new FormControl<string>(this._constantService.defaultCountry, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      district: new FormControl<string | null>(null, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      addressLine: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required]
      })
    }),
    source: new FormControl<PaymentSource>(PaymentSource.FNE, {
      nonNullable: true
    })
  });

  parseNumberFn = parseMaskToNumber;
  validatePhoneNumberFn = validatePhoneNumber;

  currentResource$ = this._globalFacade.currentResource$;

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      $event.returnValue = true;
    }
  }

  ngOnInit(): void {
    if (!this._isPlatformBrowser) return;

    this._initCitiesListener();
    this._initDistrictsListener();
    this._listenToPaymentMethodChanges();
  }

  ngAfterViewInit(): void {
    if (!this._isPlatformBrowser) return;
    this._initPhoneNumberInputs();
    this._initStates();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.form.value as PaymentCheckoutParams;

    const paymentCreationParams: PaymentCreationParams = {
      ...(payload as PaymentCreationParams),
      phoneNumber: isValidNumber(payload.phoneNumber)
        ? parsePhoneNumberWithError(payload.phoneNumber).nationalNumber
        : payload.phoneNumber
    };

    this.isSubmitting.set(true);
    this._paymentService
      .checkout(paymentCreationParams)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: response => {
            this.paymentId.set(response.id);
            this._goToStep(FormStep.step2);

            this.hasUnsavedChanges.set(true);

            this._verifyPaymentStatus(response.id);
          }
        }),
        finalize(() => {
          this.isSubmitting.set(false);
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  private readonly _pollingDelays = [
    2_000, 5_000, 10_000, 20_000, 20_000, 30_000, 33_000
  ];

  private _verifyPaymentStatus(paymentId: string): Subscription {
    return concat(...this._pollingDelays.map(delay => timer(delay)))
      .pipe(
        switchMap(() =>
          this._paymentService.read(paymentId).pipe(
            map(response => ({
              status: response.result.status,
              statusReason: response.result.statusReason
            })),
            catchError(() => {
              this._toastService.open({
                content:
                  'Erreur lors de la vérification du paiement. Veuillez réessayer.',
                type: 'error'
              });
              return of({ status: 'failed' as const, statusReason: undefined });
            })
          )
        ),
        takeUntilDestroyed(this._destroyRef),
        map((result, index) => {
          const isTerminalResult =
            result.status === 'completed' ||
            result.status === 'failed' ||
            result.status === 'canceled' ||
            result.statusReason === 'EXPIRED';

          if (index + 1 === this._pollingDelays.length && !isTerminalResult) {
            return {
              status: 'failed' as const,
              statusReason: 'EXPIRED' as const
            };
          }

          return result;
        }),
        tap({
          next: result => {
            const { status, statusReason } = result;

            if (status === 'failed') {
              if (statusReason === 'EXPIRED') {
                this.transactionStatus.set('canceled');
                this._goToStep(FormStep.step3);
                return;
              }

              this.transactionStatus.set(status);
              this._goToStep(FormStep.step3);
              return;
            }

            if (status === 'completed' || status === 'canceled') {
              this.transactionStatus.set(status);
              this._goToStep(FormStep.step3);
              return;
            }
          }
        }),
        takeWhile(
          result =>
            result.status !== 'completed' &&
            result.status !== 'failed' &&
            result.status !== 'canceled' &&
            result.statusReason !== 'EXPIRED',
          true
        ),
        finalize(() => {
          this.hasUnsavedChanges.set(false);
          this._cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  protected _initCitiesListener(): void {
    const cities$ = this._referenceDataService
      .list({
        type: 'ref_city',
        itemPerPage: 100_000_000
      })
      .pipe(map(res => res.items));
    this.citiesListener = new LoadListener(cities$);
  }

  protected _initDistrictsListener(parentValue?: string): void {
    const params = removeNilProperties({
      type: 'ref_district',
      parentValue: parentValue,
      itemPerPage: 100_000_000
    });
    const districts$ = parentValue
      ? this._referenceDataService.list(params).pipe(map(res => res.items))
      : of([]);
    this.districtsListener = new LoadListener(districts$);
  }

  private _goToStep(step: FormStep): void {
    this.currentStepIndex.set(step);
    setTimeout(() => {
      this._stepper().selectedIndex = step;
    }, 0);
  }

  private _initPhoneNumberInputs(): void {
    const inputEl = this.intlInputPhone()?.nativeElement;
    if (!inputEl) return;

    this.phoneNumberIntlPluginRef = intlTelInput(inputEl, {
      allowDropdown: true,
      formatOnDisplay: true,
      nationalMode: false,
      autoInsertDialCode: true,
      separateDialCode: true,
      initialCountry: this._constantsService.defaultCountry.toLowerCase(),
      customContainer: 'country',
      dropdownContainer: document.body
    });
    attachDialCodePrefill(inputEl, this.phoneNumberIntlPluginRef);
  }

  private _initStates(): void {
    const stepper = this._stepper();
    const setStates = (): void => {
      const count = stepper.steps.length;

      this.states =
        count > 0
          ? ['edit', ...Array<StepState>(Math.max(count - 1, 0)).fill('number')]
          : [];

      stepper.selectedIndex = 0;
      stepper.steps.forEach(s => (s.completed = false));

      this._cdRef.markForCheck();
    };

    if (stepper.steps.length === 0) {
      stepper.steps.changes
        .pipe(take(1), takeUntilDestroyed(this._destroyRef))
        .subscribe(setStates);
      return;
    }

    setStates();
  }

  private _listenToPaymentMethodChanges(): void {
    this.paymentMethodControl?.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(),
        tap(method => {
          if (this.isSandboxEnvironment && method) {
            const defaultNum = DEFAULT_STICKER_NUMBERS[method];
            if (defaultNum) {
              setTimeout(() => {
                this.form.patchValue({ phoneNumber: defaultNum });
              });
            }
          }
        })
      )
      .subscribe();
  }

  get amountControl(): AbstractControl<number | null> | null {
    return this.form.get('amount.amount');
  }

  get phoneNumberControl(): AbstractControl<string | null> | null {
    return this.form.get('phoneNumber');
  }

  get invoiceControl(): FormGroup<{
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    city: FormControl<string | null>;
    country: FormControl<string>;
    district: FormControl<string | null>;
    addressLine: FormControl<string>;
  }> | null {
    return this.form.get('invoice') as FormGroup<{
      firstName: FormControl<string>;
      lastName: FormControl<string>;
      city: FormControl<string | null>;
      country: FormControl<string>;
      district: FormControl<string | null>;
      addressLine: FormControl<string>;
    }>;
  }

  get firstNameControl(): AbstractControl<string | null> | null {
    return this.form.get('invoice.firstName');
  }

  get lastNameControl(): AbstractControl<string | null> | null {
    return this.form.get('invoice.lastName');
  }

  get cityControl(): AbstractControl<string | null> | null {
    return this.form.get('invoice.city');
  }

  get districtControl(): AbstractControl<string | null> | null {
    return this.form.get('invoice.district');
  }

  get addressLineControl(): AbstractControl<string | null> | null {
    return this.form.get('invoice.addressLine');
  }

  get paymentMethodControl(): AbstractControl<PaymentMethod | null> | null {
    return this.form.get('paymentMethod');
  }
}
